import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Apple, Bot, Infinity as InfinityIcon, Shield, X } from "lucide-react";
import { usePlatform } from "@/context/PlatformContext";
import { PLATFORM_IDS, PLATFORMS } from "@/data/platform";

const SEEN_KEY = "tracksy-platform-chosen";
const APPEAR_DELAY_MS = 1200; // long enough for the hero to load, short enough to catch intent

/**
 * First-visit platform picker. Appears once per browser, cross-faded in
 * ~1.2s after mount, asks the user to pick Apple Find My or Google Find Hub,
 * then dismisses and routes the whole site to the chosen platform.
 *
 * Returning visitors (localStorage: tracksy-platform-chosen=true) never see it.
 * Dismissing via X or Escape keeps the current default (Apple) and sets the flag.
 */
const PlatformPicker = () => {
  const { setPlatform } = usePlatform();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const alreadyChosen = window.localStorage?.getItem(SEEN_KEY) === "true";
    if (alreadyChosen) return;
    const t = setTimeout(() => setOpen(true), APPEAR_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", h);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", h);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = () => {
    setOpen(false);
    try { window.localStorage?.setItem(SEEN_KEY, "true"); } catch (_) {}
  };

  const pick = (id) => {
    setPlatform(id);
    close();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="platform-picker"
          data-testid="platform-picker-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-6 sm:px-6"
          aria-modal="true"
          role="dialog"
          aria-labelledby="platform-picker-title"
        >
          {/* Backdrop */}
          <motion.div
            onClick={close}
            className="absolute inset-0 bg-tracksy-ink/70 backdrop-blur-md"
          />

          {/* Card */}
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-[640px] overflow-hidden rounded-[1.75rem] bg-white shadow-lift"
          >
            {/* Close */}
            <button
              type="button"
              onClick={close}
              data-testid="platform-picker-close"
              aria-label="Close platform picker"
              className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full text-tracksy-sub transition-colors hover:bg-black/5 hover:text-tracksy-ink"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="px-6 pb-2 pt-10 text-center sm:px-10 sm:pb-3 sm:pt-12">
              <span
                className="inline-flex items-center gap-2 rounded-full bg-black/5 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.22em] text-tracksy-ink/80"
              >
                <InfinityIcon className="h-3 w-3" /> Unlimited range · Zero subscriptions
              </span>
              <h2
                id="platform-picker-title"
                className="mt-5 font-heading text-[26px] font-extrabold leading-tight tracking-tight text-tracksy-ink sm:text-[32px]"
              >
                Which phone do you use?
              </h2>
              <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-tracksy-sub sm:text-base">
                Pick your platform and we'll show you the right Tracksy for your device.
                You can switch anytime using the toggle at the top.
              </p>
            </div>

            {/* Choices */}
            <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2 sm:gap-4 sm:p-8">
              {PLATFORM_IDS.map((id) => {
                const p = PLATFORMS[id];
                const Icon = id === "apple" ? Apple : Bot;
                return (
                  <motion.button
                    key={id}
                    type="button"
                    data-testid={`platform-picker-${id}`}
                    onClick={() => pick(id)}
                    whileHover={{ y: -3 }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ type: "spring", stiffness: 320, damping: 22 }}
                    className="group relative flex items-center gap-4 overflow-hidden rounded-2xl border border-black/10 bg-white p-5 text-left shadow-subtle transition-colors sm:flex-col sm:items-start sm:gap-3 sm:p-6"
                    style={{ "--tile-accent": p.accent }}
                  >
                    {/* Glow swatch */}
                    <span
                      className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
                      style={{ background: p.accent }}
                    />
                    <span
                      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-white sm:h-16 sm:w-16"
                      style={{ background: p.accent }}
                    >
                      <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
                    </span>
                    <div className="flex-1 sm:mt-1">
                      <p className="font-heading text-lg font-extrabold tracking-tight text-tracksy-ink sm:text-xl">
                        {p.short}
                      </p>
                      <p className="mt-0.5 text-xs leading-relaxed text-tracksy-sub sm:text-sm">
                        Works with <span className="font-bold" style={{ color: p.accent }}>{p.network}</span>
                      </p>
                      <p className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-tracksy-ink/60 group-hover:text-tracksy-ink">
                        Choose <span className="transition-transform group-hover:translate-x-0.5">→</span>
                      </p>
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Reassurance footer */}
            <div className="flex items-center justify-center gap-2 border-t border-black/5 bg-tracksy-paper px-6 py-3.5 text-[11px] font-semibold uppercase tracking-wider text-tracksy-sub">
              <Shield className="h-3.5 w-3.5" style={{ color: "var(--brand-accent)" }} />
              Setup is 60 seconds. No subscription. No new app.
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PlatformPicker;
